import React, { useState } from "react";
import appLogo from "../assets/app-logo.png";
import { usePostHog } from 'posthog-js/react';
import { captureEvent, captureError } from "../utils/posthogUtils";
import "../Login.css";

const ForgotPasswordModal = ({ show, onClose, onEmailSubmitted }) => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const posthog = usePostHog();

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/auth/request-reset`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        captureEvent(posthog, 'Password Reset Requested', { email });
        setMessage(
          "We've sent a one-time code to your email. Please check your inbox!"
        );
        setTimeout(() => {
          onEmailSubmitted(email);
        }, 1200);
      } else {
        captureError(posthog, 'Password Reset Failed', new Error(data.message || "Error sending OTP"), { email });
        setMessage(data.message || "Error sending OTP.");
      }
    } catch (err) {
      captureError(posthog, 'Password Reset Failed', err, { email });
      setMessage("Server error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{
          padding: 0,
          borderRadius: "24px",
          boxShadow:
            "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
          maxWidth: "480px",
          width: "90%",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #6c757d 0%, #868e96 100%)",
            padding: "32px 32px 24px 32px",
            color: "white",
            textAlign: "center",
            position: "relative",
          }}
        >
          <img
            src={appLogo}
            alt="RELAY logo"
            style={{
              width: "60px",
              height: "60px",
              marginBottom: "16px",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
          />
          <h2
            style={{
              fontSize: "1.75rem",
              fontWeight: "700",
              margin: "0 0 8px 0",
              letterSpacing: "-0.025em",
            }}
          >
            Forgot Password?
          </h2>
          <p
            style={{
              fontSize: "0.95rem",
              margin: "0",
              opacity: "0.9",
              lineHeight: "1.4",
            }}
          >
            Enter your email address to reset your password.
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: "32px", background: "#fafbfc" }}>
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                htmlFor="forgot-email"
                style={{
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "4px",
                }}
              >
                Email Address
              </label>
              <input
                id="forgot-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  padding: "12px 16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "12px",
                  fontSize: "1rem",
                  transition: "all 0.2s ease",
                  backgroundColor: "#fff",
                  outline: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6c757d";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(108, 117, 125, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
                autoComplete="username"
              />
            </div>

            <div className="forgot-password-options">
              <button
                type="submit"
                disabled={loading}
                className="forgot-password-btn"
                style={{
                  padding: "12px 24px",
                  background:
                    "linear-gradient(135deg, #6c757d 0%, #868e96 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  opacity: loading ? 0.7 : 1,
                  boxShadow: "0 2px 8px rgba(108, 117, 125, 0.2)",
                }}
                onMouseOver={(e) => {
                  if (!loading) {
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(108, 117, 125, 0.3)";
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 2px 8px rgba(108, 117, 125, 0.2)";
                }}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="forgot-password-btn"
                style={{
                  padding: "12px 24px",
                  background: "#fff",
                  color: "#6c757d",
                  border: "2px solid #6c757d",
                  borderRadius: "12px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#6c757d";
                  e.target.style.color = "#fff";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "#fff";
                  e.target.style.color = "#6c757d";
                }}
              >
                Back to Login
              </button>
            </div>
          </form>

          {message && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px 16px",
                borderRadius: "8px",
                fontSize: "0.9rem",
                fontWeight: "500",
                backgroundColor: message.includes("sent")
                  ? "#d1fae5"
                  : "#fee2e2",
                color: message.includes("sent") ? "#065f46" : "#dc2626",
                border: `1px solid ${message.includes("sent") ? "#a7f3d0" : "#fecaca"
                  }`,
              }}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
